import React, { ReactElement, useRef } from "react"
import { twJoin, twMerge } from "tailwind-merge"
import Link from "next/link"
import links from "./navigation/links"
import Icon from "@components/UI/Icon"
import { useClickOutside } from "@screens/HomeV2/lib/useClickOutside"

export const NavigationBlock = ({ title, icon, description, href, theme }) => (
  <Link
    key={title}
    href={href}
    className={twJoin(
        "group",
        "flex flex-row gap-x-16p p-16p max-w-[300px]",
        "transition-colors duration-300",
        "hover:bg-neutral2-800",
        "hover:rounded-8p",
        "[.neutral_&]:hover:bg-neutral-100",
        "[.light_&]:hover:bg-neutral-100"
    )}
  >
    <div>
      <Icon
        className={""}
        defsSalt={"desk-header"}
        name={icon}
        color={theme !== "dark" ? "neutral-700" : "neutral-100"}
        size={32}
      />
    </div>
    <div className="flex flex-col gap-8p pt-8p">
      <div className="text-white [.neutral_&]:text-neutral-700 [.light_&]:text-neutral-700 text-16p leading-20p font-600 whitespace-normal [.neutral_&]:group-hover:text-violet-500 [.light_&]:group-hover:text-violet-500">{title}</div>
      <div className="text-neutral2-300 [.neutral_&]:text-neutral-700 [.light_&]:text-neutral-700 text-16p leading-24p font-400 whitespace-normal">{description}</div>
    </div>
  </Link>
)

const featureItems = (theme) => {
  return [
    <NavigationBlock
        key={`${links.SMART_TIME_TRACKING.href}-${links.SMART_TIME_TRACKING.title}`}
        href={links.SMART_TIME_TRACKING.href}
        title={links.SMART_TIME_TRACKING.title}
        icon={links.SMART_TIME_TRACKING.icon}
        description={links.SMART_TIME_TRACKING.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.TEAM_MANAGEMENT.href}-${links.TEAM_MANAGEMENT.title}`}
        href={links.TEAM_MANAGEMENT.href}
        title={links.TEAM_MANAGEMENT.title}
        icon={links.TEAM_MANAGEMENT.icon}
        description={links.TEAM_MANAGEMENT.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.CUSTOM_REPORTS.href}-${links.CUSTOM_REPORTS.title}`}
        href={links.CUSTOM_REPORTS.href}
        title={links.CUSTOM_REPORTS.title}
        icon={links.CUSTOM_REPORTS.icon}
        description={links.CUSTOM_REPORTS.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.PTO_AND_LEAVE.href}-${links.PTO_AND_LEAVE.title}`}
        href={links.PTO_AND_LEAVE.href}
        title={links.PTO_AND_LEAVE.title}
        icon={links.PTO_AND_LEAVE.icon}
        description={links.PTO_AND_LEAVE.description}
        theme={theme}
    />,
    // <NavigationBlock
    //   key={`${links.WORK_ANOMALIES.href}-${links.WORK_ANOMALIES.title}`}
    //   href={links.WORK_ANOMALIES.href}
    //   title={links.WORK_ANOMALIES.title}
    //   icon={links.WORK_ANOMALIES.icon}
    //   description={links.WORK_ANOMALIES.description}
    // />,
    <NavigationBlock
        key={`${links.ATTENDANCE_AND_TIME_CARDS.href}-${links.ATTENDANCE_AND_TIME_CARDS.title}`}
        href={links.ATTENDANCE_AND_TIME_CARDS.href}
        title={links.ATTENDANCE_AND_TIME_CARDS.title}
        icon={links.ATTENDANCE_AND_TIME_CARDS.icon}
        description={links.ATTENDANCE_AND_TIME_CARDS.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.ROLES_AND_PERMISSIONS.href}-${links.ROLES_AND_PERMISSIONS.title}`}
        href={links.ROLES_AND_PERMISSIONS.href}
        title={links.ROLES_AND_PERMISSIONS.title}
        icon={links.ROLES_AND_PERMISSIONS.icon}
        description={links.ROLES_AND_PERMISSIONS.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.PAY_RATES.href}-${links.PAY_RATES.title}`}
        href={links.PAY_RATES.href}
        title={links.PAY_RATES.title}
        icon={links.PAY_RATES.icon}
        description={links.PAY_RATES.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.CLIENTS_PROJECTS_AND_TASKS.href}-${links.CLIENTS_PROJECTS_AND_TASKS.title}`}
        href={links.CLIENTS_PROJECTS_AND_TASKS.href}
        title={links.CLIENTS_PROJECTS_AND_TASKS.title}
        icon={links.CLIENTS_PROJECTS_AND_TASKS.icon}
        description={links.CLIENTS_PROJECTS_AND_TASKS.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.TIME_AND_FINANCIAL_DASHBOARD.href}-${links.TIME_AND_FINANCIAL_DASHBOARD.title}`}
        href={links.TIME_AND_FINANCIAL_DASHBOARD.href}
        title={links.TIME_AND_FINANCIAL_DASHBOARD.title}
        icon={links.TIME_AND_FINANCIAL_DASHBOARD.icon}
        description={links.TIME_AND_FINANCIAL_DASHBOARD.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.TIMESHEETS_AND_EXPENSES.href}-${links.TIMESHEETS_AND_EXPENSES.title}`}
        href={links.TIMESHEETS_AND_EXPENSES.href}
        title={links.TIMESHEETS_AND_EXPENSES.title}
        icon={links.TIMESHEETS_AND_EXPENSES.icon}
        description={links.TIMESHEETS_AND_EXPENSES.description}
        theme={theme}
    />
  ]
}

const useCases = (theme) => {
  return [
    <NavigationBlock
        key={`${links.FOR_AGENCIES.href}-${links.FOR_AGENCIES.title}`}
        href={links.FOR_AGENCIES.href}
        title={links.FOR_AGENCIES.title}
        icon={links.FOR_AGENCIES.icon}
        description={links.FOR_AGENCIES.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.FOR_FREELANCERS.href}-${links.FOR_FREELANCERS.title}`}
        href={links.FOR_FREELANCERS.href}
        title={links.FOR_FREELANCERS.title}
        icon={links.FOR_FREELANCERS.icon}
        description={links.FOR_FREELANCERS.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.FOR_REMOTE_TEAMS.href}-${links.FOR_REMOTE_TEAMS.title}`}
        href={links.FOR_REMOTE_TEAMS.href}
        title={links.FOR_REMOTE_TEAMS.title}
        icon={links.FOR_REMOTE_TEAMS.icon}
        description={links.FOR_REMOTE_TEAMS.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.FOR_ENTERPRISES.href}-${links.FOR_ENTERPRISES.title}`}
        href={links.FOR_ENTERPRISES.href}
        title={links.FOR_ENTERPRISES.title}
        icon={links.FOR_ENTERPRISES.icon}
        description={links.FOR_ENTERPRISES.description}
        theme={theme}
    />,
    <NavigationBlock
        key={`${links.FOR_SMALL_AND_MEDIUM_BUSINESSES.href}-${links.FOR_SMALL_AND_MEDIUM_BUSINESSES.title}`}
        href={links.FOR_SMALL_AND_MEDIUM_BUSINESSES.href}
        title={links.FOR_SMALL_AND_MEDIUM_BUSINESSES.title}
        icon={links.FOR_SMALL_AND_MEDIUM_BUSINESSES.icon}
        description={links.FOR_SMALL_AND_MEDIUM_BUSINESSES.description}
        theme={theme}
    />,
  ]
}

export type NavigationSubItemsProps = {
  isVisible: boolean
  type: string | undefined
  theme: string
  closeAction: () => void
}

const MenuWrapper = ({
  children,
  title,
  description,
  href,
  descriptionClassName
}: {
  descriptionClassName?: string
  children: ReactElement
  title: string
  description: string
  href?: string
}) => (
  <div className="flex flex-row">
    <div className="flex flex-col gap-16p w-full max-w-[304px]">
      <div>
        {href ? (
          <Link
            href={href}
            className={twJoin(
              "text-24p font-600 text-white hover:underline hover:text-slate-blue-400",
              "[.neutral_&]:text-neutral-700 [.neutral_&]:hover:text-violet-500",
              "[.light_&]:text-neutral-700 [.light_&]:hover:text-violet-500"
            )}
          >
            {title}
          </Link>
        ) : (
          <div className="text-24p font-600 text-white [.neutral_&]:text-neutral-700 [.light_&]:text-neutral-700">
            {title}
          </div>
        )}
      </div>
      <div className={twMerge(
          "text-16p font-400 leading-24p text-neutral2-50",
          "[.neutral_&]:text-neutral-700",
          "[.light_&]:text-neutral-700",
          descriptionClassName
      )}>{description}</div>
    </div>
    <div className={"w-full"}>{children}</div>
  </div>
)

export const NavigationSubItems = ({ isVisible, type, closeAction, theme }: NavigationSubItemsProps) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useClickOutside({
    ref,
    callBack: () => {
      if (isVisible) {
        closeAction()
      }
    },
  })

  return (
    <div className={twJoin(isVisible ? "visible" : "hidden", "absolute z-1001 top-[55px] left-0 w-full")}>
      <div
        ref={ref}
        className={twJoin(
          "mx-auto",
          "border border-solid",
          "rounded-8p",
          theme !== "dark"
              ? "bg-white border-neutral-300 shadow-[0_4px_24px_0_rgb(29,30,44,0.1)]"
              : "bg-neutral2-900 border-neutral2-600 shadow-[0px_4px_24px_0px_rgba(29,_30,_44,_0.1)]",
          "p-40p",
          "1280:max-w-[1344px]"
        )}
      >
        {(() => {
          switch (type) {
            case "FEATURES": {
              return (
                <MenuWrapper
                  href={links.FEATURES.href}
                  title={"Features"}
                  description={
                    "See how tracked time powers reporting, invoicing, project management, and workload planning"
                  }
                >
                  <div className="flex flex-wrap flex-row">{featureItems(theme)}</div>
                </MenuWrapper>
              )
            }
            case "USE_CASES": {
              return (
                <MenuWrapper
                  href={links.USE_CASES.href}
                  title={"Use cases"}
                  description={links.USE_CASES.description}
                >
                  <div className="flex flex-wrap flex-row">{useCases(theme)}</div>
                </MenuWrapper>
              )
            }
            case "COMPANY": {
              return (
                <MenuWrapper
                  title={"Company"}
                  // для красивого переноса фразы на десктопе
                  descriptionClassName="w-[210px]"
                  description={links.COMPANY.description}
                >
                  <div className="flex flex-wrap flex-row gap-x-22p ">
                    <NavigationBlock
                      key={`${links.ABOUT_US.href}-${links.ABOUT_US.title}`}
                      href={links.ABOUT_US.href}
                      title={links.ABOUT_US.title}
                      icon={links.ABOUT_US.icon}
                      description={links.ABOUT_US.description}
                      theme={theme}
                    />
                    <NavigationBlock
                      key={`${links.CONTACT_US.href}-${links.CONTACT_US.title}`}
                      href={links.CONTACT_US.href}
                      title={links.CONTACT_US.title}
                      icon={links.CONTACT_US.icon}
                      description={links.CONTACT_US.description}
                      theme={theme}
                    />
                  </div>
                </MenuWrapper>
              )
            }
            case "RESOURCES":
              return (
                <div
                  className={"flex flex-col gap-8"}
                >
                  <MenuWrapper
                    title={"Company"}
                    description={links.COMPANY.description}
                  >
                    <div className="flex flex-wrap flex-row gap-x-22p *:flex-1">
                      <NavigationBlock
                        key={`${links.ABOUT_US.href}-${links.ABOUT_US.title}`}
                        href={links.ABOUT_US.href}
                        title={links.ABOUT_US.title}
                        icon={links.ABOUT_US.icon}
                        description={links.ABOUT_US.description}
                        theme={theme}
                      />
                      <NavigationBlock
                        key={`${links.CONTACT_US.href}-${links.CONTACT_US.title}`}
                        href={links.CONTACT_US.href}
                        title={links.CONTACT_US.title}
                        icon={links.CONTACT_US.icon}
                        description={links.CONTACT_US.description}
                        theme={theme}
                      />
                      <NavigationBlock
                        key={`${links.BLOG.href}-${links.BLOG.title}`}
                        href={links.BLOG.href}
                        title={links.BLOG.title}
                        icon={links.BLOG.icon}
                        description={links.BLOG.description}
                        theme={theme}
                      />
                    </div>
                  </MenuWrapper>
                </div>
              )
            default: {
              return null
            }
          }
        })()}
      </div>
    </div>
  )
}
